import React, { useState, useEffect, useCallback } from 'react';
import { auditLogsApi } from '../api';
import { toast } from 'sonner';
import { History, Search, Filter, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

const ACTION_LABELS = {
  CREATE: { label: 'Criação', color: 'bg-green-500/20 text-green-400 border border-green-500/30' },
  UPDATE: { label: 'Edição',  color: 'bg-blue-500/20 text-blue-400 border border-blue-500/30' },
  DELETE: { label: 'Exclusão', color: 'bg-red-500/20 text-red-400 border border-red-500/30' },
  APPROVE: { label: 'Aprovação', color: 'bg-purple-500/20 text-purple-400 border border-purple-500/30' },
  LOGIN:  { label: 'Login',  color: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' },
  LOGOUT: { label: 'Logout', color: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch { return iso; }
};

const AuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchUser, setSearchUser] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const LIMIT = 50;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await auditLogsApi.list({
        skip: page * LIMIT,
        limit: LIMIT,
        user_name: searchUser || undefined,
        module: filterModule || undefined,
        action: filterAction || undefined,
      });
      setLogs(res.data);
    } catch {
      toast.error('Erro ao carregar trilha de auditoria');
    } finally {
      setLoading(false);
    }
  }, [page, searchUser, filterModule, filterAction]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); setPage(0); load(); };

  const actionInfo = (action) => ACTION_LABELS[action] || { label: action, color: 'bg-gray-500/20 text-gray-400' };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <History size={28} className="text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Trilha de Auditoria</h1>
            <p className="text-gray-400 text-sm">Registro de todas as ações realizadas no sistema — ISO 9001:2015, RDC 658/2022</p>
          </div>
        </div>
        <button onClick={load} className="btn-secondary flex items-center gap-2">
          <RefreshCw size={16} /> Atualizar
        </button>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={searchUser}
              onChange={e => setSearchUser(e.target.value)}
              placeholder="Buscar por usuário..."
              className="input pl-9"
            />
          </div>
          <input
            value={filterModule}
            onChange={e => setFilterModule(e.target.value)}
            placeholder="Filtrar módulo..."
            className="input"
          />
          <select value={filterAction} onChange={e => setFilterAction(e.target.value)} className="input">
            <option value="">Todas as ações</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
          <button type="submit" className="btn-primary flex items-center justify-center gap-2">
            <Filter size={16} /> Filtrar
          </button>
        </div>
      </form>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <History size={48} className="mx-auto mb-3 opacity-30" />
            <p>Nenhum registro encontrado</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Data/Hora</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Usuário</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Ação</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Módulo</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Descrição</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const ai = actionInfo(log.action);
                const expanded = expandedId === log.id;
                return (
                  <React.Fragment key={log.id}>
                    <tr className="border-b border-gray-700/50 hover:bg-gray-700/20 transition-colors">
                      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                      <td className="px-4 py-3 text-sm text-white">{log.user_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded font-medium ${ai.color}`}>{ai.label}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{log.module}</td>
                      <td className="px-4 py-3 text-sm text-gray-300 max-w-xs truncate">{log.record_description || '—'}</td>
                      <td className="px-4 py-3">
                        {(log.old_values || log.new_values) && (
                          <button
                            onClick={() => setExpandedId(expanded ? null : log.id)}
                            className="text-blue-400 hover:text-blue-300 flex items-center gap-1 text-xs"
                          >
                            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {expanded ? 'Fechar' : 'Ver dados'}
                          </button>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-gray-800/50">
                        <td colSpan={6} className="px-4 py-3">
                          <div className="grid grid-cols-2 gap-4 text-xs">
                            {log.old_values && (
                              <div>
                                <p className="text-red-400 font-medium mb-1">Valores Anteriores</p>
                                <pre className="text-gray-400 bg-gray-900 rounded p-2 overflow-auto max-h-40">
                                  {JSON.stringify(log.old_values, null, 2)}
                                </pre>
                              </div>
                            )}
                            {log.new_values && (
                              <div>
                                <p className="text-green-400 font-medium mb-1">Valores Novos</p>
                                <pre className="text-gray-400 bg-gray-900 rounded p-2 overflow-auto max-h-40">
                                  {JSON.stringify(log.new_values, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-gray-400">
        <span>Mostrando {page * LIMIT + 1}–{page * LIMIT + logs.length} registros</span>
        <div className="flex gap-2">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="btn-secondary disabled:opacity-40 px-3 py-1.5 text-sm">Anterior</button>
          <button disabled={logs.length < LIMIT} onClick={() => setPage(p => p + 1)} className="btn-secondary disabled:opacity-40 px-3 py-1.5 text-sm">Próxima</button>
        </div>
      </div>
    </div>
  );
};

export default AuditTrail;
